/* Atrium — the real-time nervous system (Matrix /sync long-poll), typed.

   One long-poll loop streams deltas and fans them out to typed subscribers:
     • message  (roomId, event) — m.room.message timeline events → chat
     • state    ()              — a state event changed → (debounced) floor refresh
     • member   (roomId, event) — m.room.member changes in joined rooms (room knocks)
     • invite   (roomId)        — you were invited to a room (knock admitted)
     • toDevice (event)         — knock / grab signals
     • error    (err)           — a sync request failed (loop backs off + retries)
*/
import { matrix } from '@/lib/matrix'
import type { MatrixEvent, SyncResponse } from '@/types/matrix'

type Handlers = {
  message: (roomId: string, ev: MatrixEvent) => void
  state: () => void
  member: (roomId: string, ev: MatrixEvent) => void
  invite: (roomId: string) => void
  toDevice: (ev: MatrixEvent) => void
  custom: (roomId: string, ev: MatrixEvent) => void // app.atrium.* timeline events (ticket comments / log)
  stateEv: (roomId: string, ev: MatrixEvent) => void // individual state events WITH payload (room features)
  reaction: (roomId: string, ev: MatrixEvent) => void // m.reaction annotations + m.room.redaction (chip aggregation)
  error: (err: unknown) => void
}
type EventName = keyof Handlers

const subs: { [K in EventName]: Set<Handlers[K]> } = {
  message: new Set(),
  state: new Set(),
  member: new Set(),
  invite: new Set(),
  toDevice: new Set(),
  custom: new Set(),
  stateEv: new Set(),
  reaction: new Set(),
  error: new Set(),
}

let stopped = true

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

function on<K extends EventName>(evt: K, cb: Handlers[K]): () => void {
  subs[evt].add(cb)
  return () => subs[evt].delete(cb)
}

function emitMessage(roomId: string, ev: MatrixEvent) {
  subs.message.forEach((cb) => {
    try { cb(roomId, ev) } catch (e) { console.warn('[sync] message handler', e) }
  })
}
function emitState() {
  subs.state.forEach((cb) => {
    try { cb() } catch (e) { console.warn('[sync] state handler', e) }
  })
}
function emitMember(roomId: string, ev: MatrixEvent) {
  subs.member.forEach((cb) => {
    try { cb(roomId, ev) } catch (e) { console.warn('[sync] member handler', e) }
  })
}
function emitInvite(roomId: string) {
  subs.invite.forEach((cb) => {
    try { cb(roomId) } catch (e) { console.warn('[sync] invite handler', e) }
  })
}
function emitToDevice(ev: MatrixEvent) {
  subs.toDevice.forEach((cb) => {
    try { cb(ev) } catch (e) { console.warn('[sync] toDevice handler', e) }
  })
}
function emitCustom(roomId: string, ev: MatrixEvent) {
  subs.custom.forEach((cb) => {
    try { cb(roomId, ev) } catch (e) { console.warn('[sync] custom handler', e) }
  })
}
function emitStateEv(roomId: string, ev: MatrixEvent) {
  subs.stateEv.forEach((cb) => {
    try { cb(roomId, ev) } catch (e) { console.warn('[sync] stateEv handler', e) }
  })
}
function emitReaction(roomId: string, ev: MatrixEvent) {
  subs.reaction.forEach((cb) => {
    try { cb(roomId, ev) } catch (e) { console.warn('[sync] reaction handler', e) }
  })
}
function emitError(err: unknown) {
  subs.error.forEach((cb) => {
    try { cb(err) } catch (e) { console.warn('[sync] error handler', e) }
  })
}

function dispatch(data: SyncResponse) {
  const td = data.to_device?.events
  if (td) for (const ev of td) emitToDevice(ev)

  const join = data.rooms?.join || {}
  let stateChanged = false
  for (const roomId in join) {
    const room = join[roomId]
    // Emit only the LATEST member event per user: a batch (especially the
    // initial sync, which replays recent timeline) can contain a knock that
    // was already answered by a later invite/join in the same batch — the
    // stale knock must not resurface. The state section is the state at the
    // start of the timeline, so timeline events override it in order.
    const latestMember = new Map<string, MatrixEvent>()
    if (room.state?.events?.length) {
      stateChanged = true
      for (const ev of room.state.events || []) {
        if (typeof ev.state_key === 'string') emitStateEv(roomId, ev)
        if (ev.type === 'm.room.member' && typeof ev.state_key === 'string') latestMember.set(ev.state_key, ev)
      }
    }
    const tl = room.timeline?.events
    if (tl) {
      for (const ev of tl) {
        if (ev.type === 'm.room.message') emitMessage(roomId, ev)
        else if (ev.type === 'm.reaction' || ev.type === 'm.room.redaction') emitReaction(roomId, ev)
        else if (typeof ev.state_key === 'string') {
          stateChanged = true
          emitStateEv(roomId, ev)
          if (ev.type === 'm.room.member') latestMember.set(ev.state_key, ev)
        } else if (ev.type.startsWith('app.atrium.')) emitCustom(roomId, ev)
      }
    }
    latestMember.forEach((ev) => emitMember(roomId, ev))
  }
  for (const roomId in data.rooms?.invite || {}) emitInvite(roomId)
  if (stateChanged) emitState()
}

async function loop() {
  while (!stopped) {
    let data: SyncResponse
    try {
      data = await matrix.sync(30000)
    } catch (e) {
      emitError(e)
      if (stopped) break
      await sleep(3000)
      continue
    }
    if (stopped) break
    try {
      dispatch(data)
    } catch (e) {
      console.warn('[sync] dispatch failed', e)
    }
  }
}

function start() {
  if (!stopped) return
  stopped = false
  void loop()
}
function stop() {
  stopped = true
}

export const sync = { start, stop, on, isRunning: () => !stopped }
